import React, { useState, useEffect } from 'react';
import { Mail, Phone, Calendar, MessageSquare, Check, X, Building2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import it from 'date-fns/locale/it';

function ContactsList() {
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select(`
          *,
          property:property_id (
            title,
            reference_number
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Errore nel caricamento dei contatti');
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (contactId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('contacts')
        .update({ 
          status,
          notes: notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', contactId);
      
      if (error) throw error;
      
      toast.success('Stato aggiornato con successo');
      fetchContacts();
      setSelectedContact(null);
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Errore nell\'aggiornamento dello stato');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'contacted':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return 'Nuovo';
      case 'contacted':
        return 'Contattato';
      case 'closed':
        return 'Chiuso';
      default:
        return status;
    }
  };

  return (
    <AdminLayout title="Gestione Contatti">
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Contatti dal Sito</h2>
        <p className="text-gray-600">Gestisci le richieste di contatto ricevute dal sito web</p>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento contatti...</p>
        </div>
      ) : contacts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessun contatto</h3>
          <p className="text-gray-500">
            Non ci sono ancora richieste di contatto da gestire
          </p>
        </div>
      ) : (
        <div className="grid gap-6">
          {contacts.map((contact) => (
            <div 
              key={contact.id} 
              className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-4">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {contact.name}
                    </h3>
                    <p className="text-gray-500 text-sm">
                      {format(new Date(contact.created_at), "d MMMM yyyy 'alle' HH:mm", { locale: it })}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium self-start flex-shrink-0 ${getStatusColor(contact.status)}`}>
                    {getStatusText(contact.status)}
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center text-gray-600 min-w-0">
                    <Mail className="h-5 w-5 mr-2 text-gray-400 flex-shrink-0" />
                    <a href={`mailto:${contact.email}`} className="hover:text-primary break-all">
                      {contact.email}
                    </a>
                  </div>
                  
                  {contact.phone && (
                    <div className="flex items-center text-gray-600 min-w-0">
                      <Phone className="h-5 w-5 mr-2 text-gray-400 flex-shrink-0" />
                      <a href={`tel:${contact.phone}`} className="hover:text-primary break-all">
                        {contact.phone}
                      </a>
                    </div>
                  )}
                </div>
                
                {contact.property && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start text-gray-600">
                      <Building2 className="h-5 w-5 mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="min-w-0 break-words">
                        Riferimento: {contact.property.reference_number} - {contact.property.title}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{contact.message}</p>
                </div>
                
                {contact.notes && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-700 mb-2">Note</h4>
                    <p className="text-gray-600">{contact.notes}</p>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
                  {contact.status === 'new' && (
                    <button
                      onClick={() => setSelectedContact(contact)}
                      className="bg-primary text-white px-4 py-2.5 rounded-md hover:bg-primary-light transition-colors flex items-center justify-center"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Segna come contattato
                    </button>
                  )}
                  {contact.status === 'contacted' && (
                    <button
                      onClick={() => setSelectedContact(contact)}
                      className="bg-green-600 text-white px-4 py-2.5 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Chiudi richiesta
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Status Update Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-md w-full my-auto max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">
              {selectedContact.status === 'new' ? 'Segna come contattato' : 'Chiudi richiesta'}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note (opzionali)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                placeholder="Aggiungi note sul contatto..."
              />
            </div>
            
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                onClick={() => {
                  setSelectedContact(null);
                  setNotes('');
                }}
                className="px-4 py-2.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={() => updateContactStatus(
                  selectedContact.id,
                  selectedContact.status === 'new' ? 'contacted' : 'closed'
                )}
                className="px-4 py-2.5 bg-primary text-white rounded-md hover:bg-primary-light transition-colors"
              >
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ContactsList;